import { Button, FormLabel, Paper, Popover, Stack, styled, TextField } from '@mui/material';
import { CustomCellEditorProps } from 'ag-grid-react';
import { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { parseNumber } from 'src/utils/formatNumber';
import { SalesCampaignDiscountCellValue } from 'src/utils/salesCampaignDiscount';

interface TouchedFields {
    x: boolean;
    y: boolean;
    z: boolean;
}

export const DiscountCellEditor: FC<CustomCellEditorProps<unknown, SalesCampaignDiscountCellValue>> = ({
    value,
    initialValue,
    onValueChange,
    eventKey,
    eGridCell,
    stopEditing,
}) => {
    const discountType = value?.discountType;
    const isPriceDiscountType = discountType === 'FIXED_PRICE' || discountType === 'PERCENTAGE';
    const valueX = value?.x ?? '';
    const valueY = value?.y ?? '';
    const valueZ = value?.z ?? '';
    const [touchedFields, setTouchedFields] = useState<TouchedFields>({
        x: false,
        y: false,
        z: false,
    });

    const validation = useMemo(() => {
        const x = parseNumber(valueX);
        const y = parseNumber(valueY);
        const z = parseNumber(valueZ);

        let xError = false;
        let yError = false;
        let zError = false;
        let zRangeError = false;

        if (discountType === 'BUY_X_GET_Y_PERCENT_OFF_Z') {
            // For BUY_X_GET_Y_PERCENT_OFF_Z: x > 1, 0 < y < 100, z > 0, x > z
            xError = !Number.isInteger(x) || x <= 1;
            yError = !Number.isInteger(y) || y <= 0 || y >= 100;
            zError = !Number.isInteger(z) || z <= 0;
            zRangeError = Number.isInteger(x) && Number.isInteger(z) && x <= z;
        } else {
            // x is always have to be whole number and more than 0 (FIXED_PRICE is default set to 1)
            xError = !isPriceDiscountType && (!Number.isInteger(x) || x <= 0);

            if (discountType === 'X_FOR_Y' || discountType === 'X_FOR_FIXED_PRICE') {
                // For the legacy multi-value types, both x and y must be whole numbers > 0
                yError = !Number.isInteger(y) || y <= 0;
            }
        }

        return {
            isValid: !xError && !yError && !zError && !zRangeError,
            xError,
            yError,
            zError,
            zRangeError,
        };
    }, [discountType, isPriceDiscountType, valueX, valueY, valueZ]);

    const handleUpdate = useCallback(
        (property: keyof SalesCampaignDiscountCellValue, newValue: string) => {
            setTouchedFields((currentTouchedFields) => ({
                ...currentTouchedFields,
                [property]: true,
            }));

            const update = { [property]: newValue };
            onValueChange({
                x: value?.x,
                y: value?.y,
                z: value?.z,
                discountType: value?.discountType,
                ...update,
            });
        },
        [onValueChange, value]
    );

    const handleFieldBlur = useCallback((field: keyof TouchedFields) => {
        setTouchedFields((currentTouchedFields) => ({
            ...currentTouchedFields,
            [field]: true,
        }));
    }, []);

    const inputRef = useRef<HTMLInputElement | null>(null);

    const handlePopoverClose = useCallback(() => {
        onValueChange(initialValue);
        stopEditing();
    }, [stopEditing, onValueChange, initialValue]);

    useLayoutEffect(() => {
        const eventKeyAsNumber = Number(eventKey);
        if (eventKey && !Number.isNaN(eventKeyAsNumber)) {
            onValueChange({
                x: 1,
                y: eventKeyAsNumber,
                z: undefined,
                discountType: 'FIXED_PRICE',
            });
            return;
        }

        setTimeout(() => {
            inputRef.current?.select();
        });
    }, [eventKey, onValueChange]);

    const handleEnterKey = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.stopPropagation();
                if (validation.isValid) {
                    stopEditing();
                }
            }
        },
        [stopEditing, validation.isValid]
    );

    return (
        <>
            {isPriceDiscountType ? (
                <EditorTextField
                    inputRef={inputRef}
                    autoFocus
                    fullWidth
                    placeholder="Enter price"
                    onChange={(event) => handleUpdate('y', event.target.value)}
                    value={valueY ?? ''}
                />
            ) : (
                <>
                    {/* When discount type is set, we always have two values X and Y, one represents amount and the other represents price */}
                    {
                        <Popover
                            className="ag-custom-component-popup"
                            anchorEl={eGridCell}
                            open
                            sx={{ zIndex: 9 }}
                            onClose={handlePopoverClose}
                            onKeyDown={handleEnterKey}
                        >
                            <StyledPaper elevation={6}>
                                <Stack
                                    sx={{
                                        gap: '0.75rem',
                                    }}
                                >
                                    <Stack
                                        sx={{
                                            gap: '0.25rem',
                                        }}
                                    >
                                        <StyledFormLabel>Amount</StyledFormLabel>
                                        <EditorTextField
                                            inputRef={inputRef}
                                            fullWidth
                                            placeholder="Enter amount"
                                            onChange={(event) => handleUpdate('x', event.target.value)}
                                            onBlur={() => handleFieldBlur('x')}
                                            value={valueX ?? ''}
                                            error={touchedFields.x && validation.xError}
                                            helperText={
                                                touchedFields.x && validation.xError
                                                    ? discountType === 'BUY_X_GET_Y_PERCENT_OFF_Z'
                                                        ? 'Must be a whole number greater than 1'
                                                        : 'Must be a whole number greater than 0'
                                                    : ''
                                            }
                                        />
                                    </Stack>
                                    <Stack
                                        sx={{
                                            gap: '0.25rem',
                                        }}
                                    >
                                        <StyledFormLabel>
                                            {discountType === 'X_FOR_Y'
                                                ? 'Amount'
                                                : discountType === 'BUY_X_GET_Y_PERCENT_OFF_Z'
                                                  ? 'Percent (%)'
                                                  : 'Price'}
                                        </StyledFormLabel>
                                        <EditorTextField
                                            fullWidth
                                            placeholder={
                                                discountType === 'X_FOR_Y'
                                                    ? 'Enter amount'
                                                    : discountType === 'BUY_X_GET_Y_PERCENT_OFF_Z'
                                                      ? 'Enter percent'
                                                      : 'Enter price'
                                            }
                                            onChange={(event) => handleUpdate('y', event.target.value)}
                                            onBlur={() => handleFieldBlur('y')}
                                            value={valueY ?? ''}
                                            error={touchedFields.y && validation.yError}
                                            helperText={
                                                touchedFields.y && validation.yError
                                                    ? discountType === 'X_FOR_Y' || discountType === 'X_FOR_FIXED_PRICE'
                                                        ? 'Must be a whole number greater than 0'
                                                        : discountType === 'BUY_X_GET_Y_PERCENT_OFF_Z'
                                                          ? 'Must be a whole number greater than 0 and less than 100'
                                                          : ''
                                                    : ''
                                            }
                                        />
                                    </Stack>
                                    {discountType === 'BUY_X_GET_Y_PERCENT_OFF_Z' && (
                                        <Stack
                                            sx={{
                                                gap: '0.25rem',
                                            }}
                                        >
                                            <StyledFormLabel>Items (Z)</StyledFormLabel>
                                            <EditorTextField
                                                fullWidth
                                                placeholder="Enter items count"
                                                onChange={(event) => handleUpdate('z', event.target.value)}
                                                onBlur={() => handleFieldBlur('z')}
                                                value={valueZ ?? ''}
                                                error={touchedFields.z && (validation.zError || validation.zRangeError)}
                                                helperText={
                                                    touchedFields.z && validation.zError
                                                        ? 'Must be a whole number greater than 0'
                                                        : touchedFields.z && validation.zRangeError
                                                          ? 'Must be less than X'
                                                          : ''
                                                }
                                            />
                                        </Stack>
                                    )}
                                    <Button
                                        variant="outlined"
                                        onClick={() => stopEditing()}
                                        disabled={!validation.isValid}
                                    >
                                        OK
                                    </Button>
                                </Stack>
                            </StyledPaper>
                        </Popover>
                    }
                </>
            )}
        </>
    );
};

const StyledPaper = styled(Paper)`
  background-color: #fff;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 12px;
`;

const EditorTextField = styled(TextField)`
  .MuiInputBase-root {
    font-size: inherit;
    border-radius: 4px;
  }

  .Mui-focused fieldset {
    border-color: rgba(33, 150, 243, 0.4) !important;
  }

  .MuiInputBase-input {
    padding: 8px 14px;
  }
`;

const StyledFormLabel = styled(FormLabel)`
  font-size: inherit;
`;
